import type { CommunityId, GameSettings, LeaderboardEntry } from '../types';
import { RAFFLE_COPY } from '../../config/raffleConfig';
import { clearRaffleEntries } from './RaffleSystem';

const SETTINGS_KEY = 'connect-360:settings:v2';
const LEADERBOARD_KEY = 'packet-runner-360:leaderboard:v1';
const EVENT_READY_MIGRATION_KEY = 'connect-360:event-ready-migration:v1';

export const DEFAULT_SETTINGS: GameSettings = {
  eventMode: true,
  audioEnabled: true,
  masterVolume: 0.65,
  gameDuration: 75,
  difficulty: 'normal',
  quickPlayOnly: false,
  enabledLevels: { 'level-1': true, 'level-2': true, 'level-3': true },
  controlMode: 'auto',
  controllerVibration: true,
  inactivityTimeout: 60,
  highContrast: false,
  reducedMotion: false,
  showFps: false,
  qrDestination: 'https://turncom360.com/academy',
  primaryGameMode: 'quick',
  availableCommunities: {
    henderson: true,
    oxford: true,
    louisburg: true,
    enfield: true,
    'warren-county': true,
    'another-community': true,
  },
  bonusRoundEnabled: true,
  mayorChallengeEnabled: true,
  autoStart: false,
  textSize: 'standard',
  raffleStaffInstruction: RAFFLE_COPY.staffInstruction,
};

export function loadSettings(): GameSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (!saved) return structuredClone(DEFAULT_SETTINGS);
    const parsed = JSON.parse(saved) as Partial<GameSettings>;
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      enabledLevels: { ...DEFAULT_SETTINGS.enabledLevels, ...parsed.enabledLevels },
      availableCommunities: { ...DEFAULT_SETTINGS.availableCommunities, ...parsed.availableCommunities },
    };
  } catch {
    return structuredClone(DEFAULT_SETTINGS);
  }
}

export function saveSettings(settings: GameSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function loadLeaderboard(): LeaderboardEntry[] {
  try {
    const saved = localStorage.getItem(LEADERBOARD_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved) as LeaderboardEntry[];
    return Array.isArray(parsed)
      ? parsed.slice(0, 25).map((entry) => ({
        ...entry,
        highestCombo: Number(entry.highestCombo) || 0,
        community: typeof entry.community === 'string' ? entry.community : 'Another Community',
        mode: entry.mode ?? 'packet',
      })).filter((entry) => entry.score > 0)
      : [];
  } catch {
    return [];
  }
}

export function addLeaderboardEntry(entry: LeaderboardEntry): LeaderboardEntry[] {
  const entries = [...loadLeaderboard(), entry]
    .sort((a, b) => b.score - a.score || a.completionTime - b.completionTime)
    .slice(0, 25);
  localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(entries));
  return entries;
}

export function updateLeaderboardEntry(createdAt: string, updates: Partial<Pick<LeaderboardEntry, 'score' | 'highestCombo'>>): LeaderboardEntry[] {
  const entries = loadLeaderboard()
    .map((entry) => entry.createdAt === createdAt ? { ...entry, ...updates } : entry)
    .sort((a, b) => b.score - a.score || a.completionTime - b.completionTime)
    .slice(0, 25);
  localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(entries));
  return entries;
}

export function resetLeaderboard(): void {
  localStorage.removeItem(LEADERBOARD_KEY);
}

export function prepareForEvent(): void {
  resetLeaderboard();
  clearRaffleEntries();
  localStorage.setItem(EVENT_READY_MIGRATION_KEY, 'complete');
}

export function performEventReadyMigration(): void {
  if (localStorage.getItem(EVENT_READY_MIGRATION_KEY) === 'complete') return;
  const cleaned = loadLeaderboard().filter((entry) => entry.initials.toUpperCase() !== 'WIN');
  localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(cleaned));
  clearRaffleEntries();
  localStorage.setItem(EVENT_READY_MIGRATION_KEY, 'complete');
}

export function resetCommunityScores(community?: string): void {
  if (!community) {
    const packetScores = loadLeaderboard().filter((entry) => entry.mode === 'packet');
    localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(packetScores));
    return;
  }
  const entries = loadLeaderboard().filter((entry) => entry.community !== community);
  localStorage.setItem(LEADERBOARD_KEY, JSON.stringify(entries));
}

export function enabledCommunityIds(settings: GameSettings): CommunityId[] {
  return (Object.keys(settings.availableCommunities) as CommunityId[]).filter((id) => settings.availableCommunities[id]);
}
